import { z } from "zod";
import { machineStatusSchema, machineTypeSchema } from "@packsize/schemas/machines";

import { productionStatusSchema } from "../machine-group/production-status";

import { trackSchema } from "./track";
import { machineSecurePrintConfiguration } from "./machine-secure-print-configuration";

// TODO(schema-unification): Base machine schemas for errors/warnings need alignment across machine types
// Current state:
// - Base: machineErrorSchema (message), machineWarningSchema (message)
// - PackTags: machinePacktagsErrorSchema (alarm, alarmNumber), machinePacktagsWarningSchema (alarm, alarmNumber)
// - X4/X7: machineAlarmSchema (passthrough) - needs proper definition
// - Zebra: zebraMachineErrorSchema (arguments[], code, error, machineId, message, tenantId, utcDateTime)
// - Lantech: machineAlarmSchema (message, code, argument, emergencyStop - recursive)
const machineWarningSchema = z.object({
	message: z.string(),
});

const machineSettingsSchema = z.object({}).passthrough();

const machinePacktagsWarningSchema = z.object({
	alarm: z.number().optional(),
	alarmNumber: z.number().optional(),
});

const machineErrorSchema = z.object({
	message: z.string().optional(),
	code: z.number().optional(),
	// Arguments can be numbers or strings depending on the machine type
	arguments: z.array(z.union([z.number(), z.string()])).optional(),
	error: z.string().optional(),
	machineId: z.string().optional(),
	tenantId: z.string().optional(),
	iotEdgeDeviceId: z.string().optional(),
});

const machinePacktagsErrorSchema = z.object({
	alarm: z.number().optional(),
	alarmNumber: z.number().optional(),
});

export const machineRequestSchema = z.object({
	alias: z.string(),
	description: z.string().optional(),
	ipOrDnsName: z.string().optional(),
	port: z.number().optional(),
});

export const machineSchema = z.object({
	// Core fields (required)
	id: z.string(),
	alias: z.string(),
	tenantId: z.string(),
	machineType: machineTypeSchema,
	status: machineStatusSchema,
	productionStatus: productionStatusSchema,

	description: z
		.string()
		.nullish()
		.transform((val) => val ?? undefined)
		.optional(),
	ipOrDnsName: z.string().optional(),
	port: z.number().optional(),
	assetId: z
		.union([z.string(), z.number()])
		.nullish()
		.transform((val) => (val != null ? String(val) : undefined))
		.optional(),
	plcVersion: z.string().nullish(),
	hasPackTags: z.boolean().optional(),

	// IoT fields (used in machine info popup)
	iotEdgeDeviceId: z.string().optional().nullable(),
	proofOfLifeUpdated: z.string().optional().nullable(),
	lastProofOfLifeTimeStamp: z.string().optional().nullable(),

	// Machine settings
	physicalMachineSettings: machineSettingsSchema.nullish(),

	// Error/warning fields (used in widgets/status display)
	error: machineErrorSchema.optional(),
	warnings: z.array(machineWarningSchema).optional(),
	activeErrors: z.array(machinePacktagsErrorSchema).optional(),
	activeWarnings: z.array(machinePacktagsWarningSchema).optional(),

	// Machine-specific fields (used in service/config)
	tracks: z.array(trackSchema).optional(),
	settings: z
		.object({
			hasEfiPrinter: z.boolean().optional(),
			hasFlapsDownModule: z.boolean().optional(),
			designId: z.number().optional(),
		})
		.optional(),
	packMlState: z.string().nullish(),
	packMlMode: z.string().nullish(),
	activeStopReason: z
		.object({
			isActive: z.boolean(),
			message: z.string().optional(),
		})
		.optional(),
	securePrintConfiguration: machineSecurePrintConfiguration.nullish(),
});

export type Machine = z.infer<typeof machineSchema>;
